/**
 * How long a finished run took, start to end.
 *
 * A run that moves its eSIM spends minutes on setup before its flow starts,
 * and older runs recorded `result.duration` from the flow's start only. When
 * the run's first setup step came before that, count from the step, so the
 * finished time matches what the live timer showed. Never shorter than the
 * recorded duration.
 */
export function wholeRunMs(opts: {
  durationMs?: number;
  startedAt?: Date | null;
  completedAt?: Date | null;
  /** Earliest setup step time (ms), if the run has any. */
  firstStepAt?: number;
}): number | undefined {
  const { durationMs, startedAt, completedAt, firstStepAt } = opts;
  const recorded = typeof durationMs === 'number' && durationMs > 0 ? durationMs : undefined;
  const flowStart = startedAt?.getTime();
  const end = completedAt?.getTime()
    ?? (flowStart !== undefined && recorded !== undefined ? flowStart + recorded : undefined);
  const starts = [flowStart, firstStepAt].filter((t): t is number => typeof t === 'number');
  if (end === undefined || starts.length === 0) return recorded;
  const span = end - Math.min(...starts);
  if (!(span > 0)) return recorded;
  return recorded !== undefined ? Math.max(recorded, span) : span;
}

/** Earliest start (or end) time among a run's steps, in ms. */
export function firstStepTime(steps?: Array<{ startedAt?: Date; endedAt?: Date }>): number | undefined {
  const times = (steps ?? [])
    .map((s) => (s.startedAt ?? s.endedAt)?.getTime())
    .filter((t): t is number => typeof t === 'number');
  return times.length ? Math.min(...times) : undefined;
}
